import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FontStyle } from '../../../items/text/text-item-type';
import { changeItemsWithSelection } from '../../../state/actions/change-items';
import { editAndRelayoutText } from '../../../state/actions/edit-and-relayout-text';
import { setTextItemHoverPreview } from '../../../state/actions/set-hover-preview';
import {
  getFontVariants,
  loadFontInfoFromApi,
} from '../../../utils/text/load-font-from-text-item';
import { useWriteContext } from '../../../utils/use-context';
import { useTargetItemIds } from '../../utils/use-target-item-ids';
import { useSelectionValues } from '../../utils/use-selection-values';
import {
  FontStyleSelectionItem,
  renderWeight,
  serializeFontStyle,
} from './font-style-selection-item';
import { useTranslations } from 'next-intl';
import { InspectorSubLabel } from '@/features/remotion-editor/editor/inspector/components/inspector-label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
} from '@crabs-ai/ds-react';
import { TextItem } from '../../../items/text/text-item-type';
import { CaptionsItem } from '../../../items/captions/captions-item-type';
import { useTextSelection } from '../../../contexts/text-selection-context';

// segments 내 폰트 스타일이 혼합되어 있는지 확인
const MIXED_FONT_STYLE_MARKER = '__MIXED_FONT_STYLE__';

const getFontStyleFromTextItem = (item: TextItem | CaptionsItem): string => {
  if (item.type !== 'text' || !item.segments || item.segments.length === 0) {
    return serializeFontStyle(item.fontStyle);
  }

  // 모든 segment의 fontWeight/fontVariant 수집 (없으면 item.fontStyle 사용)
  const styles = item.segments.map((seg) => {
    const weight = seg.fontWeight ?? item.fontStyle.weight;
    const variant = seg.fontVariant ?? item.fontStyle.variant;
    return `${variant}-${weight}`;
  });
  const firstStyle = styles[0];
  const allSame = styles.every((s) => s === firstStyle);

  return allSame ? firstStyle! : MIXED_FONT_STYLE_MARKER;
};

const FontStyleControlsUnmemoized: React.FC<{
  fontFamily: string;
  fontStyle: FontStyle;
  itemId: string;
  disableCharacterStyleEditing?: boolean;
}> = ({
  fontFamily,
  fontStyle,
  itemId,
  disableCharacterStyleEditing = false,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isMultiItemMixed, value: selectedFontStyle } =
    useSelectionValues<TextItem | CaptionsItem, string>(
      itemId,
      getFontStyleFromTextItem,
    );
  const [variants, setVariants] = useState<FontStyle[][] | null>([]);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // 선택 영역 내 Mixed 마커
  const SELECTION_MIXED = '__SELECTION_MIXED__';

  const {
    selectionStyle,
    applyFontStyle: applyFontStyleToSelection,
    previewFontStyle: previewFontStyleToSelection,
    cancelFontStylePreview,
  } = useTextSelection();

  // 최종 Mixed 여부 결정
  // 1. 편집 모드에서 텍스트 선택 시 → selectionStyle.fontWeight 또는 fontVariant가 Mixed 마커인지 확인
  // 2. 그 외 → 여러 아이템 혼합 또는 segments 내 혼합
  const isMixed = selectionStyle.hasSelection
    ? selectionStyle.fontWeight === SELECTION_MIXED ||
      selectionStyle.fontVariant === SELECTION_MIXED
    : isMultiItemMixed || selectedFontStyle === MIXED_FONT_STYLE_MARKER;

  // 표시할 weight 결정
  const displayWeight = selectionStyle.hasSelection
    ? selectionStyle.fontWeight === SELECTION_MIXED
      ? null
      : (selectionStyle.fontWeight ?? fontStyle.weight)
    : fontStyle.weight;

  // Popover 열릴 때 선택 상태 저장
  const hadSelectionOnOpenRef = useRef(false);
  // Popover 열릴 때 원본 스타일 저장 (체크박스 고정용)
  const originalStyleOnOpenRef = useRef<FontStyle | null>(null);

  useEffect(() => {
    setVariants(null);
    loadFontInfoFromApi(fontFamily)
      .then((infos) => {
        console.log('infos', infos);
        const loadedVariants = getFontVariants(infos);
        setVariants(loadedVariants);
      })

      .catch(console.error);
  }, [fontFamily]);

  useEffect(() => {
    if (!isOpen) return;

    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;
      let current: HTMLElement | null = el.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          current.scrollHeight > current.clientHeight
        ) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const scrollParent = getScrollParent(triggerRef.current);
    if (!scrollParent) return;

    const handleScroll = () => setIsOpen(false);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  const applyFontStyle = useCallback(
    (value: string) => {
      const [variant, weight] = value.split('-') as [
        FontStyle['variant'],
        FontStyle['weight'],
      ];

      // 텍스트 선택이 있었으면 선택 영역에만 적용
      if (hadSelectionOnOpenRef.current) {
        applyFontStyleToSelection({ weight, variant });
        return;
      }

      setState({
        update: (state) => {
          const newState = changeItemsWithSelection(
            state,
            itemId,
            targetIds,
            (i) => {
              if (i.type === 'text') {
                return editAndRelayoutText(i, () => {
                  if (
                    i.fontStyle.variant === variant &&
                    i.fontStyle.weight === weight
                  ) {
                    return i;
                  }

                  // segments에서 fontWeight, fontVariant 속성 제거
                  let newSegments = i.segments?.map((seg) => {
                    const { fontWeight: _w, fontVariant: _v, ...rest } = seg;
                    return rest;
                  });
                  // 모든 segment가 빈 객체(text만 있는)면 segments 제거
                  const hasStyledSegments = newSegments?.some(
                    (s) =>
                      s.color ||
                      s.fontFamily ||
                      s.fontSize ||
                      s.opacity !== undefined,
                  );
                  if (!hasStyledSegments) {
                    newSegments = undefined;
                  }

                  return {
                    ...i,
                    fontStyle: {
                      variant,
                      weight,
                    },
                    segments: newSegments,
                  };
                });
              }
              if (i.type === 'captions') {
                return {
                  ...i,
                  fontStyle: {
                    variant,
                    weight,
                  },
                };
              }

              throw new Error(
                `Font style can only be changed for text and captions items`,
              );
            },
          );

          return setTextItemHoverPreview({
            state: newState,
            hoverPreview: null,
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds, applyFontStyleToSelection],
  );

  // 선택 후 Popover 닫기
  const handleFontStyleSelect = useCallback(
    (value: string) => {
      applyFontStyle(value);
      // cancelFontStylePreview가 호출되지 않도록 false로 설정
      hadSelectionOnOpenRef.current = false;
      setIsOpen(false);
    },
    [applyFontStyle],
  );

  const resetFontStyle = useCallback(() => {
    // 텍스트 선택이 있었으면 selection preview 취소
    if (hadSelectionOnOpenRef.current) {
      cancelFontStylePreview();
      return;
    }

    setState({
      update: (state) => {
        return setTextItemHoverPreview({
          state,
          hoverPreview: null,
        });
      },
      commitToUndoStack: false,
    });
  }, [setState, cancelFontStylePreview]);

  const previewFontStyle = useCallback(
    (newFontStyle: FontStyle) => {
      // 텍스트 선택이 있었으면 selection preview 사용
      if (hadSelectionOnOpenRef.current) {
        previewFontStyleToSelection({
          weight: newFontStyle.weight,
          variant: newFontStyle.variant,
        });
        return;
      }

      setState({
        update: (state) => {
          return {
            ...state,
            textItemHoverPreview: {
              itemId,
              type: 'font-style',
              fontStyle: newFontStyle,
            },
          };
        },
        commitToUndoStack: false,
      });
    },
    [setState, itemId, previewFontStyleToSelection],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (open) {
        // disableCharacterStyleEditing이 true면 선택 상태를 무시
        hadSelectionOnOpenRef.current = disableCharacterStyleEditing
          ? false
          : selectionStyle.hasSelection;
        originalStyleOnOpenRef.current = fontStyle;
      } else {
        // Popover 닫힐 때 preview 취소
        if (hadSelectionOnOpenRef.current) {
          cancelFontStylePreview();
        }
        hadSelectionOnOpenRef.current = false;
        originalStyleOnOpenRef.current = null;
      }
    },
    [
      selectionStyle.hasSelection,
      fontStyle,
      cancelFontStylePreview,
      disableCharacterStyleEditing,
    ],
  );

  if (!variants) {
    return null;
  }

  return (
    <div className="flex-1">
      <InspectorSubLabel>{t('Editor.inspector.text.weight')}</InspectorSubLabel>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger asChild>
          <div ref={triggerRef}>
            <SelectFieldTrigger
              value={renderWeight(displayWeight ?? fontStyle.weight)}
              hasMixedValue={isMixed}
              onMouseDown={(e) => {
                if (selectionStyle.hasSelection) {
                  e.preventDefault();
                }
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="left"
          sideOffset={8}
          align="start"
          collisionPadding={16}
          className="shadow-basic-md max-h-[287px] w-[160px] overflow-y-auto rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 p-0 px-3xs py-2xs"
          onMouseDown={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => {
            // 글자 선택 상태일 때 autoFocus 방지 (selection 유지)
            if (hadSelectionOnOpenRef.current) {
              e.preventDefault();
            }
          }}
        >
          {variants.map((variantGroup, i) => (
            <React.Fragment key={i}>
              {variantGroup.map((variant) => {
                // 텍스트 선택이 있을 때는 원본 스타일로 체크박스 고정
                const styleForSelected =
                  originalStyleOnOpenRef.current || fontStyle;
                return (
                  <FontStyleSelectionItem
                    key={serializeFontStyle(variant)}
                    variant={variant}
                    fontFamily={fontFamily}
                    applyFontStyle={handleFontStyleSelect}
                    previewFontStyle={previewFontStyle}
                    resetFontStyle={resetFontStyle}
                    selected={
                      styleForSelected.weight === variant.weight &&
                      styleForSelected.variant === variant.variant
                    }
                  />
                );
              })}
            </React.Fragment>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const FontStyleControls = memo(FontStyleControlsUnmemoized);
